import React, { useState, useEffect, useRef } from 'react';
import {
  Tags, Plus, Trash2, FileText,
  AlertCircle, Check, X, Tag as TagIcon
} from 'lucide-react';
import { adminApi } from '../../lib/adminApi';
import { Button } from '../../components/ui/button';
import BlogNavTabs from './BlogNavTabs';
import './AdminBlog.css';

export default function AdminBlogTags() {
  const [tags, setTags] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const [newTagName, setNewTagName] = useState('');
  const [adding, setAdding] = useState(false);
  const inputRef = useRef(null);

  const [deleteConfirmTag, setDeleteConfirmTag] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const fetchTags = () => {
    setLoading(true);
    setError('');
    adminApi.get('/admin/blog-tags')
      .then(res => setTags(res.data || []))
      .catch(err => setError(err.response?.data?.detail || "Failed to load tags."))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    fetchTags();
  }, []);

  const handleAddTag = async (e, tagArg) => {
    if (e && e.preventDefault) {
      e.preventDefault();
      e.stopPropagation();
    }
    const raw = (typeof tagArg === 'string' ? tagArg : newTagName).trim();
    if (!raw) {
      setError("Please enter a tag name (e.g. SEO Optimization).");
      inputRef.current?.focus();
      return;
    }

    const tagsToAdd = raw.split(',')
      .map(t => t.trim().replace(/^#+/, ''))
      .filter(t => t.length > 0);

    if (tagsToAdd.length === 0) {
      setError("Please enter a valid tag name.");
      inputRef.current?.focus();
      return;
    }

    setAdding(true);
    setError('');
    try {
      for (const tag of tagsToAdd) {
        await adminApi.post('/admin/blog-tags', { name: tag });
      }
      setSuccess(tagsToAdd.length === 1 ? `Tag "#${tagsToAdd[0]}" added successfully.` : `${tagsToAdd.length} tags added successfully.`);
      setNewTagName('');
      
      // Optimistically update tags list immediately
      setTags(prev => {
        const next = [...prev];
        tagsToAdd.forEach(name => {
          if (!next.some(t => t.name.toLowerCase() === name.toLowerCase())) {
            next.unshift({ name, slug: name.toLowerCase().replace(/[^\w\s-]/g, '').replace(/[\s_-]+/g, '-'), post_count: 0 });
          }
        });
        return next;
      });

      fetchTags();
      setTimeout(() => setSuccess(''), 4500);
    } catch (err) {
      setError(err.response?.data?.detail || "Failed to add tag. Please try again.");
    } finally {
      setAdding(false);
    }
  };

  const handleDeleteTag = async (tagName) => {
    setDeleting(true);
    try {
      await adminApi.delete(`/admin/blog-tags/${encodeURIComponent(tagName)}`);
      setSuccess(`Tag "#${tagName}" removed.`);
      setDeleteConfirmTag(null);
      setTags(prev => prev.filter(t => t.name !== tagName));
      fetchTags();
      setTimeout(() => setSuccess(''), 4000);
    } catch (err) {
      setError("Failed to delete tag.");
    } finally {
      setDeleting(false);
    }
  };

  return (
    <main className="space-y-6">
      <BlogNavTabs
        title="Tags"
        description="View and manage topic tags used across all blog posts."
        showCreateButton={false}
      />

      {/* Add Tag Bar */}
      <div className="bg-white p-5 border border-slate-200 rounded-xl shadow-sm">
        <form onSubmit={handleAddTag} className="space-y-3">
          <div className="flex flex-col sm:flex-row gap-3 items-stretch sm:items-center justify-between">
            <div>
              <h3 className="text-base font-bold text-slate-900">Add New Tag</h3>
              <p className="text-xs text-slate-500">
                Tags help visitors and search engines discover related articles by subject.
              </p>
            </div>

            <div className="flex items-center gap-2">
              <input
                ref={inputRef}
                type="text"
                placeholder="e.g. SEO Optimization"
                value={newTagName}
                onChange={(e) => {
                  setNewTagName(e.target.value);
                  if (error) setError('');
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    handleAddTag(e);
                  }
                }}
                className="text-sm border border-slate-300 rounded-lg px-3 py-2 focus:border-amber-500 focus:outline-none min-w-[220px]"
              />
              <Button
                type="submit"
                disabled={adding}
                onClick={handleAddTag}
                className="admin-primary text-xs font-semibold px-4 flex items-center gap-1.5"
              >
                <Plus size={15} />
                <span>{adding ? 'Adding...' : 'Add Tag'}</span>
              </Button>
            </div>
          </div>

          <div className="flex items-center gap-1.5 flex-wrap text-xs text-slate-500 pt-1 border-t border-slate-100">
            <span className="text-[11px] font-medium text-slate-400">Suggestions:</span>
            {['SEO Optimization', 'AI Search', 'ChatGPT Search', 'Brand Authority', 'Local SEO'].map(s => (
              <button
                key={s}
                type="button"
                onClick={(e) => handleAddTag(e, s)}
                className="inline-flex items-center px-2.5 py-0.5 rounded-full bg-slate-100 hover:bg-amber-100 text-slate-700 hover:text-amber-900 transition-colors text-[11px]"
              >
                + {s}
              </button>
            ))}
          </div>
        </form>
      </div>

      {/* Alerts */}
      {error && (
        <div role="alert" className="p-4 bg-red-50 border border-red-200 text-red-700 rounded-lg flex items-center gap-2 text-sm">
          <AlertCircle size={16} />
          <span>{error}</span>
        </div>
      )}
      {success && (
        <div role="status" className="p-4 bg-emerald-50 border border-emerald-200 text-emerald-800 rounded-lg flex items-center gap-2 text-sm">
          <Check size={16} />
          <span>{success}</span>
        </div>
      )}

      {/* Loading */}
      {loading && (
        <div className="bg-white border rounded-xl p-8 text-center text-slate-500">
          <div className="admin-spinner mb-3" />
          <p className="text-sm">Loading tags...</p>
        </div>
      )}

      {/* Tags List */}
      {!loading && tags.length === 0 && (
        <div className="bg-white border border-dashed border-slate-300 rounded-xl p-12 text-center text-slate-500 space-y-3">
          <Tags size={36} className="mx-auto text-slate-300" />
          <h4 className="text-base font-semibold text-slate-800">No tags created yet</h4>
          <p className="text-xs text-slate-500">Add tags above or enter tags when writing blog posts.</p>
        </div>
      )}

      {!loading && tags.length > 0 && (
        <div className="bg-white border border-slate-200 rounded-xl p-6 shadow-sm space-y-4">
          <h4 className="text-xs font-bold uppercase tracking-wider text-slate-400">
            Active Tags ({tags.length})
          </h4>

          <div className="flex flex-wrap gap-2.5">
            {tags.map((tag) => (
              <div
                key={tag.name}
                className="inline-flex items-center gap-2 bg-slate-50 hover:bg-slate-100 border border-slate-200 rounded-lg px-3 py-1.5 text-xs text-slate-800 transition-colors"
              >
                <TagIcon size={13} className="text-amber-600" />
                <span className="font-semibold">{tag.name}</span>
                <span className="bg-white text-slate-500 border border-slate-200 px-1.5 py-0.5 rounded text-[10px] font-mono">
                  {tag.post_count || 0}
                </span>
                <button
                  type="button"
                  onClick={() => setDeleteConfirmTag(tag.name)}
                  className="text-slate-400 hover:text-red-600 transition-colors ml-0.5"
                  title="Delete tag"
                >
                  <X size={13} />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ── Modal: Delete Confirmation ── */}
      {deleteConfirmTag && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-sm w-full p-6 space-y-4">
            <h4 className="font-bold text-slate-900 text-base">Delete tag &ldquo;{deleteConfirmTag}&rdquo;?</h4>
            <p className="text-xs text-slate-600 leading-relaxed">
              This will unassign this tag from all blog posts. Are you sure you want to proceed?
            </p>
            <div className="flex justify-end gap-2 pt-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setDeleteConfirmTag(null)}
              >
                Cancel
              </Button>
              <Button
                size="sm"
                disabled={deleting}
                onClick={() => handleDeleteTag(deleteConfirmTag)}
                className="admin-danger"
              >
                {deleting ? 'Deleting...' : 'Delete Tag'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </main>
  );
}
